import { ConfigProvider, Modal } from "antd";
import styled, { createGlobalStyle } from "styled-components";
import { device } from "../lib/breakpoints";

/** Edge-to-edge modal on small viewports (Ant wrap default padding causes “white bars”). */
const ModalMobileFullBleed = createGlobalStyle`
  @media ${device.mobile} {
    .clumse-modal-mobile-fullbleed.ant-modal-wrap {
      padding: 0 !important;
      display: flex !important;
      align-items: stretch !important;
    }

    .clumse-modal-mobile-fullbleed .ant-modal {
      width: 100vw !important;
      max-width: 100vw !important;
      margin: 0 !important;
      top: 0 !important;
      max-height: none !important;
      padding-bottom: 0 !important;
    }

    .clumse-modal-mobile-fullbleed .ant-modal-content {
      min-height: 100dvh !important;
      border-radius: 0 !important;
    }
  }
`;

const CustomModal = ({
  isOpen,
  onClose,
  children,
  showCloseIcon = true,
  isCentered = false,
  width = 395,
  fullScreenOnMobile = false,
  maskClosable = true,
  keyboard = true,
  /** White close icon and frosted control; use when the X sits on a green/dark header. */
  lightCloseForDarkHeader = false,
  /** Slate-950 shell + light close; use for login and similar industrial dark panels. */
  dark = false,
  styles: userStyles,
  wrapClassName: userWrapClassName,
  ...rest
}) => {
  const useLightClose = dark || lightCloseForDarkHeader;
  const mergedStyles =
    dark || userStyles
      ? {
          ...userStyles,
          ...(dark
            ? {
                mask: {
                  ...(userStyles?.mask && typeof userStyles.mask === "object"
                    ? userStyles.mask
                    : {}),
                  backgroundColor: "rgba(0, 0, 0, 0.78)",
                },
              }
            : {}),
        }
      : undefined;

  const wrapClassName = [userWrapClassName, fullScreenOnMobile && "clumse-modal-mobile-fullbleed"]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <ModalMobileFullBleed />
      <ConfigProvider
        theme={{
          token: {
            colorIcon: useLightClose
              ? "rgba(255, 255, 255, 0.95)"
              : "var(--color-text)",
          },
        }}
      >
        <StyledModal
          $dark={dark}
          $lightCloseIcon={useLightClose}
          open={isOpen}
          onCancel={onClose}
          footer={[]}
          closable={showCloseIcon}
          centered={isCentered}
          width={width}
          maskClosable={maskClosable}
          keyboard={keyboard}
          styles={mergedStyles}
          wrapClassName={wrapClassName || undefined}
          {...rest}
        >
          {children}
        </StyledModal>
      </ConfigProvider>
    </>
  );
};

export const ModalTitle = styled.div`
  color: var(--color-text);
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
`;

export const ModalDescription = styled.p`
  color: #949ba4;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.4rem;
`;

const StyledModal = styled(Modal)`
  .ant-modal-close {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    position: relative;
    top: unset;
    inset-inline-end: unset;
    margin-left: auto;
    line-height: 0 !important;
    color: ${(p) => (p.$dark ? "#f8fafc" : "#1e293b")} !important;
    background: ${(p) =>
      p.$dark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)"} !important;
    border: ${(p) =>
      p.$dark
        ? "1px solid rgba(255, 255, 255, 0.18)"
        : "1px solid rgba(0, 0, 0, 0.1)"} !important;
    z-index: 10;
    font-size: 1.5rem;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    transition:
      background 0.2s,
      color 0.2s,
      border-color 0.2s;

    &,
    .anticon,
    .anticon svg,
    .ant-modal-close-x {
      color: ${(p) => (p.$dark ? "#f8fafc" : "#1e293b")} !important;
      fill: ${(p) => (p.$dark ? "#f8fafc" : "#1e293b")} !important;
    }

    .ant-modal-close-x {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 100% !important;
      height: 100% !important;
      line-height: 0 !important;
      margin: 0 !important;
    }

    .ant-modal-close .anticon {
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      vertical-align: 0 !important;
    }

    .ant-modal-close .anticon svg {
      display: block;
    }

    &:hover {
      color: ${(p) => (p.$dark ? "#ffffff" : "#0f172a")} !important;
      background: ${(p) =>
        p.$dark ? "rgba(255, 87, 34, 0.2)" : "rgba(0, 0, 0, 0.07)"} !important;
      border-color: ${(p) =>
        p.$dark
          ? "rgba(255, 87, 34, 0.55)"
          : "rgba(0, 0, 0, 0.16)"} !important;
    }

    .anticon:hover,
    &:hover .anticon,
    &:hover .anticon svg {
      color: ${(p) => (p.$dark ? "#ffffff" : "#0f172a")} !important;
      fill: ${(p) => (p.$dark ? "#ffffff" : "#0f172a")} !important;
    }
  }

  .ant-modal-content {
    border-radius: 10px;
    // border: 2px solid var(--color-gray-100);
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
    background: white;
  }

  ${(p) =>
    p.$dark &&
    `
    .ant-modal-content {
      background: #0f172a !important;
      border: 1px solid #334155 !important;
      box-shadow: 0 24px 64px rgba(0, 0, 0, 0.55) !important;
    }
  `}

  .ant-modal-header {
    border-bottom: none;
  }

  .ant-modal-footer {
    display: none;
  }

  ${(props) =>
    props.$lightCloseIcon &&
    `
    .ant-modal-close {
      color: #ffffff !important;
      background: rgba(255, 255, 255, 0.12) !important;
      border: 1px solid rgba(255, 255, 255, 0.25) !important;
    }
    .ant-modal-close,
    .ant-modal-close .anticon,
    .ant-modal-close .anticon svg,
    .ant-modal-close .ant-modal-close-x,
    .ant-modal-close svg {
      color: #ffffff !important;
      fill: #ffffff !important;
    }
    .ant-modal-close:hover {
      color: #ffffff !important;
      background: rgba(255, 255, 255, 0.2) !important;
      border-color: rgba(255, 255, 255, 0.35) !important;
    }
    .ant-modal-close:hover .anticon,
    .ant-modal-close:hover .anticon svg,
    .ant-modal-close:hover svg {
      color: #ffffff !important;
      fill: #ffffff !important;
    }
  `}

`;

export default CustomModal;
